import { installedApps } from "@norn/widget-renderer/model"
import { useInfiniteQuery, useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo, useSyncExternalStore } from "react"
import { type Schemas, keys, run, scopeQuery } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import { useLocale } from "@/lib/i18n"
import { parseExact } from "@/lib/lossless"
import { useDefinitions } from "@/lib/queries"
import { useCell } from "@/lib/session"
import { usePrincipal } from "@/widget/runtime"
import { type Ontology, readOntology, recordTitle } from "./ontology"

export type ObjectResponse = Schemas["ObjectResponse"]

/** The business ontology of the admitted definition set, read once per set. */
export function useOntology(scope: Scope): { ontology: Ontology | undefined; defs: ReturnType<typeof useDefinitions> } {
  const defs = useDefinitions(scope)
  const ontology = useMemo(() => {
    if (!defs.data) return undefined
    const apps = installedApps(defs.data).map((app) => ({ appId: app.appId, entryPageId: app.ux.app?.entryPageId, pageNames: app.ux.pages.map((p) => p.name) }))
    return readOntology(defs.data, apps)
  }, [defs.data])
  return { ontology, defs }
}

function objectQuery(baseUrl: string, client: ReturnType<typeof useCell>["client"], scope: Scope, objectType: string, key: string) {
  return {
    queryKey: keys.object(baseUrl, scope, objectType, key),
    queryFn: async ({ signal }: { signal: AbortSignal }) =>
      parseExact(
        await run(baseUrl, () =>
          client.GET("/v1/objects/{object_type}/{key}", {
            params: { path: { object_type: objectType, key }, query: { ...scopeQuery(scope), includeDeleted: true } },
            parseAs: "text",
            signal,
          }),
        ),
      ) as ObjectResponse,
    staleTime: 60_000,
    retry: false,
  }
}

/** Authorized reads of several objects (previews, references). Each one is still authorized by the cell. */
export function useObjectsByKey(scope: Scope, refs: readonly { objectType: string; key: string }[]) {
  const { connection, client } = useCell()
  const unique = useMemo(() => {
    const seen = new Set<string>()
    return refs.filter((r) => {
      const id = `${r.objectType}/${r.key}`
      if (seen.has(id)) return false
      seen.add(id)
      return true
    })
  }, [refs])
  const results = useQueries({ queries: unique.map((r) => objectQuery(connection.baseUrl, client, scope, r.objectType, r.key)) })
  return useMemo(() => {
    const map = new Map<string, (typeof results)[number]>()
    unique.forEach((r, i) => {
      const result = results[i]
      if (result) map.set(`${r.objectType}/${r.key}`, result)
    })
    return map
  }, [unique, results])
}

/** The human title of a record by reference, with its key as the honest fallback. */
export function useTitleOf(ontology: Ontology | undefined) {
  const { preference } = useLocale()
  return useCallback(
    (object: ObjectResponse | undefined, fallbackKey: string) => {
      if (!object) return { title: fallbackKey, isKey: true, hidden: false }
      const type = ontology?.types.get(object.objectType)
      const t = recordTitle(type, object.values as Record<string, unknown>, object.masked as Record<string, unknown> | undefined, preference.locale)
      return { title: t.title ?? object.key, isKey: t.title === null, hidden: t.hidden }
    },
    [ontology, preference.locale],
  )
}

/**
 * The first page of one type, re-read while the page is open: every `everyMs(data)` milliseconds
 * (e.g. quickly while a materialized metric is still pending, slowly once current). Each re-read is
 * an ordinary authorized read; a status changes on screen only when the cell answers with it.
 */
export function useLiveObjects(scope: Scope, objectType: string, limit: number, everyMs: (data: Schemas["ObjectList"] | undefined) => number) {
  const { connection, client } = useCell()
  const queryClient = useQueryClient()
  const queryKey = ["ws-live", connection.baseUrl, scope.orgId, scope.environmentId, objectType, limit]
  const query = useQuery<Schemas["ObjectList"]>({
    queryKey,
    refetchInterval: (query) => everyMs(query.state.data),
    refetchIntervalInBackground: false,
    queryFn: async ({ signal }) =>
      parseExact(
        await run(connection.baseUrl, () =>
          client.GET("/v1/objects/{object_type}", {
            params: { path: { object_type: objectType }, query: { ...scopeQuery(scope), limit } },
            parseAs: "text",
            signal,
          }),
        ),
      ) as Schemas["ObjectList"],
  })
  // Query revisions advance on successful reads even when structural sharing keeps
  // the same data object or the wall clock is unchanged/rewound during a restore.
  return { ...query, readRevision: queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0 }
}

/** Authorized pages of one type, accumulated for a list the reader scrolls and filters. */
export function useObjectPages(scope: Scope, objectType: string, pageSize = 50, enabled = true) {
  const { connection, client } = useCell()
  return useInfiniteQuery({
    queryKey: ["ws-pages", connection.baseUrl, scope.orgId, scope.environmentId, objectType, pageSize],
    enabled,
    initialPageParam: null as string | null,
    getNextPageParam: (last: Schemas["ObjectList"]) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    queryFn: async ({ pageParam, signal }) =>
      parseExact(
        await run(connection.baseUrl, () =>
          client.GET("/v1/objects/{object_type}", {
            params: { path: { object_type: objectType }, query: { ...scopeQuery(scope), limit: pageSize, ...(pageParam ? { cursor: pageParam } : {}) } },
            parseAs: "text",
            signal,
          }),
        ),
      ) as Schemas["ObjectList"],
  })
}

export function useLinks(scope: Scope, objectType: string, key: string, enabled = true) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: ["ws-links", connection.baseUrl, scope.orgId, scope.environmentId, objectType, key],
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      // Up to four authorized pages (100 edges); counts past that are shown as "100+".
      const items: Schemas["LinkEdge"][] = []
      let masked: Record<string, unknown> = {}
      let cursor: string | null = null
      let pages = 0
      do {
        const page = parseExact(
          await run(connection.baseUrl, () =>
            client.GET("/v1/objects/{object_type}/{key}/links", {
              params: { path: { object_type: objectType, key }, query: { ...scopeQuery(scope), direction: "both", limit: 25, ...(cursor ? { cursor } : {}) } },
              parseAs: "text",
              signal,
            }),
          ),
        ) as Schemas["LinkPage"]
        items.push(...page.items)
        if (page.masked) masked = { ...masked, ...page.masked }
        cursor = page.nextCursor
        pages += 1
      } while (cursor && pages < 4)
      return { items, more: cursor !== null, masked }
    },
  })
}

// ── Journey: the trail of records opened by following links, kept per tab. ──────────────────
// History is partitioned by cell, resolved principal and scope: titles one identity could read are
// never shown to another identity or in another scope.

export interface JourneyStep {
  readonly objectType: string
  readonly key: string
  readonly title: string
}

const JOURNEY_KEY = "norn.admin.journey/2"
const RECENT_KEY = "norn.admin.recent-records/2"
const listeners = new Set<() => void>()
const caches = new Map<string, { raw: string | null; value: JourneyStep[] }>()

function snapshot(storage: Storage, key: string): JourneyStep[] {
  const raw = storage.getItem(key)
  const cached = caches.get(key)
  if (cached && cached.raw === raw) return cached.value
  let value: JourneyStep[]
  try {
    value = raw ? (JSON.parse(raw) as JourneyStep[]) : []
  } catch {
    value = []
  }
  caches.set(key, { raw, value })
  return value
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => listeners.delete(l)
}

const same = (a: { objectType: string; key: string }, b: { objectType: string; key: string }) => a.objectType === b.objectType && a.key === b.key
const EMPTY: JourneyStep[] = []

/** The history partition for this cell, principal and scope; null until the cell resolves who is reading. */
export function useHistorySpace(scope: Scope): string | null {
  const { connection } = useCell()
  const principal = usePrincipal(scope)
  return principal.status === "ready" ? `${connection.baseUrl}#${principal.actor}/${scope.orgId}/${scope.environmentId}` : null
}

export const journey = {
  /** Following a link from the current record extends the trail; anything else starts a new one. */
  open(space: string, step: JourneyStep, follow: boolean) {
    const journeyKey = `${JOURNEY_KEY}/${space}`
    const recentKey = `${RECENT_KEY}/${space}`
    const trail = snapshot(sessionStorage, journeyKey)
    const at = trail.findIndex((s) => same(s, step))
    let next: JourneyStep[]
    if (at !== -1) next = trail.slice(0, at + 1).map((s, i) => (i === at ? step : s))
    else if (follow && trail.length) next = [...trail, step].slice(-8)
    else next = [step]
    sessionStorage.setItem(journeyKey, JSON.stringify(next))
    const recent = [step, ...snapshot(localStorage, recentKey).filter((s) => !same(s, step))].slice(0, 12)
    localStorage.setItem(recentKey, JSON.stringify(recent))
    for (const l of listeners) l()
  },
}

export function useJourney(space: string | null): JourneyStep[] {
  const get = useCallback(() => (space ? snapshot(sessionStorage, `${JOURNEY_KEY}/${space}`) : EMPTY), [space])
  return useSyncExternalStore(subscribe, get, () => EMPTY)
}

export function useRecent(space: string | null): JourneyStep[] {
  const get = useCallback(() => (space ? snapshot(localStorage, `${RECENT_KEY}/${space}`) : EMPTY), [space])
  return useSyncExternalStore(subscribe, get, () => EMPTY)
}

const INDEX_LIMIT = 500
const FALLBACK_LIMIT = 60

/**
 * Resolve references to records through one authorized page per target type (master data such as
 * customers and items is small), falling back to single authorized reads for keys past that page.
 * Keeps a list of 50 orders to two or three reads instead of a hundred.
 */
export function useReferenceIndex(scope: Scope, refs: readonly { objectType: string; key: string }[]) {
  const { connection, client } = useCell()
  const types = useMemo(() => [...new Set(refs.map((r) => r.objectType))], [refs])
  const lists = useQueries({
    queries: types.map((objectType) => ({
      queryKey: [...keys.objects(connection.baseUrl, scope, objectType, null), INDEX_LIMIT],
      staleTime: 300_000,
      retry: false,
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        parseExact(
          await run(connection.baseUrl, () =>
            client.GET("/v1/objects/{object_type}", {
              params: { path: { object_type: objectType }, query: { ...scopeQuery(scope), limit: INDEX_LIMIT } },
              parseAs: "text",
              signal,
            }),
          ),
        ) as Schemas["ObjectList"],
    })),
  })
  const byType = new Map(types.map((t, i) => [t, lists[i]]))
  const seen = new Set<string>()
  const fallback = refs
      .filter((r) => {
        const id = `${r.objectType}/${r.key}`
        if (seen.has(id)) return false
        seen.add(id)
        const list = byType.get(r.objectType)
        if (!list || list.isPending) return false
        if (list.isError) return true
        return !list.data.items.some((o) => o.key === r.key) && list.data.nextCursor !== null
      })
      .slice(0, FALLBACK_LIMIT)
  const singles = useObjectsByKey(scope, fallback)
  return (objectType: string, key: string): { data?: ObjectResponse; isPending: boolean; isError: boolean; error?: unknown } => {
    const single = singles.get(`${objectType}/${key}`)
    if (single) return { data: single.data, isPending: single.isPending, isError: single.isError, error: single.error }
    const list = byType.get(objectType)
    if (!list || list.isPending) return { isPending: true, isError: false }
    // A refused index read stays visible for every reference no single read resolved.
    if (list.isError) return { isPending: false, isError: true, error: list.error }
    const found = list.data?.items.find((o) => o.key === key)
    return { data: found, isPending: false, isError: false }
  }
}
